import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { LoaderWrapper } from "../../components/index";

// Import all JD sections
import SubmittedApplicationSection from "./application/SubmittedApplication";
import InActiveApplications from "./application/InActiveApplications";
import ExtendDays from "./application/ExtendDays";
import ApplicationView from "./application/ApplicationView";
import ApplicationHistory from "./application/ApplicationHistory";
import AppDashboard from "./application/AppDashboard";
import Form8 from "./application/Form8";
import Form9 from "./application/Form9";
import Form11 from "../JD/application/Form11";
import Form11_new from "./application/Form11_new";
import Form72 from "./application/Form72";
import Form73Report from "./application/Form73";
import FormTestWitness from "./application/FormTestwitness";
import NCMainReport from "./application/NCMainReport";
import AccountsDetails from "../Do/application/AccountsDetails";
import NCApplicationFinding from "./AssessmentReport/NCApplicationFinding";
import Form217Preview from "./AssessmentReport/Form217Report";
import AdditionalAppDashboard from "./application/AdditionalAppDashboard";
import ILACAppDashboard from "./application/ILACAppDashboard";
import RequestForm11 from "./application/RequestForm11";
import OutstandingLedger from "../Do/OutstandingLedger";


const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const JDLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentSection, setCurrentSection] = useState(
    "submitted-applications",
  );
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  // Section configuration
  const sections = useMemo(
    () => [
      {
        id: "submitted-applications",
        title: "Group Applications",
        component: SubmittedApplicationSection,
        path: "/main/jd/dashboard",
      },
      {
        id: "inactive-applications",
        title: "InActve Applications",
        component: InActiveApplications,
        path: "/main/jd/inactive",
      },
      {
        id: "extend-days",
        title: "Extend Days",
        component: ExtendDays,
        path: "/main/jd/extend",
      },
      {
        id: "application-view",
        title: "Application Details",
        component: ApplicationView,
        path: "/main/jd/applications/view",
      },
      {
        id: "appdashboard-view",
        title: "AppDashboard",
        component: AppDashboard,
        path: "/main/jd/applications/appdashboard",
      },
      {
        id: "application-history",
        title: "Application History",
        component: ApplicationHistory,
        path: "/main/jd/applications/history",
      },
      {
        id: "form8",
        title: "Form 8 - Assessor Allotment",
        component: Form8,
        path: "/main/jd/application/form8",
      },
      {
        id: "form9",
        title: "Form 9 - Assessor Allotment",
        component: Form9,
        path: "/main/jd/application/form9",
      },
      {
        id: "form11",
        title: "Form 11",
        component: Form11,
        path: "/main/jd/form11",
      },
      {
        id: "form11_new",
        title: "Form 11",
        component: Form11_new,
        path: "/main/jd/form11-new",
      },
      {
        id: "accounts-details",
        title: "Accounts Details",
        component: AccountsDetails,
        path: "/main/jd/accounts-details",
      },
      {
        id: "Form217-Preview",
        title: "Form217 Preview",
        component: Form217Preview,
        path: "/main/jd/form217-preview",
      },
      {
        id: "NC Application",
        title: "NC Application",
        component: NCApplicationFinding,
        path: "/main/jd/nc-application",
      },
      {
        id: "form72",
        title: "Form 72",
        component: Form72,
        path: "/main/jd/form72",
      },
      {
        id: "form73",
        title: "Form 73",
        component: Form73Report,
        path: "/main/jd/form73",
      },
      {
        id: "FormTestWitness",
        title: "FormTestWitness",
        component: FormTestWitness,
        path: "/main/jd/FormTestWitness",
      },
      {
        id: "NC Report",
        title: "NC Report",
        component: NCMainReport,
        path: "/main/jd/nc-report",
      },
      {
        id: "additional-app-dashboard",
        title: "Additional App Dashboard",
        component: AdditionalAppDashboard,
        path: "/main/jd/additional-app-dashboard",
      },
      {
        id: "ilac-app-dashboard",
        title: "ILAC App Dashboard",
        component: ILACAppDashboard,
        path: "/main/jd/ilac-app-dashboard",
      },
      {
        id: "request-form11",
        title: "Request Form 11",
        component: RequestForm11,
        path: "/main/jd/request-form11",
      },
      {
        id: "outstanding-ledger",
        title: "Outstanding Amount & Ledger",
        component: OutstandingLedger,
        path: "/main/jd/outstanding-ledger",
      },
    ],
    [],
  );

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find((s) => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };

    setFormData(updatedFormData);
  };

  const navigateToSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const renderCurrentSection = () => {
    console.log("🔍 JDLayout renderCurrentSection:", {
      currentSection,
      pathname: location.pathname,
      availableSections: sections.map((s) => ({ id: s.id, path: s.path })),
    });

    const section = sections.find((s) => s.id === currentSection);
    if (!section) {
      console.log("❌ Section not found for currentSection:", currentSection);
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            Section not found: {currentSection}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current path: {location.pathname}
          </Typography>
        </Box>
      );
    }

    console.log("✅ Rendering section:", section.title, section.component.name);
    const SectionComponent = section.component;

    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
      />
    );
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{ display: "flex", height: "100vh", pt: `${APP_HEADER_HEIGHT}px` }}
    >
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: "flex",
            flexDirection: "column",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: "hidden",
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={0}
          onSectionClick={(item) => navigate(item.path)}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : "100%" },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: "relative",
        }}
      >
        {/* Toggle Sidebar Button for Mobile when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: "fixed",
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: "100%" }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper
              loading={true}
              type="page"
              message="Loading application data..."
            />
          )}

          <Paper sx={{ minHeight: "calc(100vh - 280px)", mb: 3 }}>
            {renderCurrentSection()}
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper
              loading={true}
              type="inline"
              message="Saving form data..."
              size="small"
            />
          )}
        </Container>
      </Box>

      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JDLayout;
